import { Outlet, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { MessagesSquare } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { Tabs, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { PushInboxHint } from '@/features/push-notifications';
import { session } from '@/shared/session/session';

import { InboxList } from '../components/inbox-list';
import { InboxLiveProvider } from '../components/inbox-live-provider';
import { InboxToolbar } from '../components/inbox-toolbar';
import { VisitorsPanel } from '../components/visitors-panel';
import type { VisitorsScope } from '../hooks/use-live-visitors';
import {
  InboxScopeContext,
  orgInboxScope,
  storeInboxScope,
  type InboxScope,
} from '../lib/inbox-scope';
import {
  INBOX_VIEWS,
  inboxView,
  scopedStoreIds,
  type InboxSearch,
  type InboxView as InboxPane,
} from '../schemas/inbox-search';

const ORG_ROUTE = '/_authed/_active/org/inbox' as const;
const STORE_ROUTE = '/_authed/_active/s/$storeId/inbox' as const;

interface InboxViewProps {
  scope: InboxScope;
  search: InboxSearch;
  onSearchChange: (patch: Partial<InboxSearch>) => void;
}

/**
 * Three panes on wide screens (list · thread · context), two on tablets (context in a sheet), one
 * on phones (the list until a conversation opens). The list pane switches between conversations
 * and the live visitor list (LC7), which spreads over the thread pane until a thread opens. One
 * socket for as long as this is mounted.
 */
function InboxView({ scope, search, onSearchChange }: InboxViewProps) {
  const { t } = useTranslation('conversations');
  const { me } = session.useSession();
  const params = useParams({ strict: false });
  const selectedId = params.conversationId;
  const stores = useMemo(
    () => (me?.stores ?? []).map((store) => ({ id: store.id, name: store.name })),
    [me?.stores],
  );
  const view = inboxView(search);
  // The store's own inbox reads its store's list; the org-wide one a single org request, narrowed
  // by the store filter when there is one.
  const visitorsScope = useMemo<VisitorsScope>(() => {
    if (scope.pinnedStoreId) return { kind: 'store', storeId: scope.pinnedStoreId };
    const filtered = search.storeId ? scopedStoreIds(search, stores) : undefined;
    return {
      kind: 'org',
      storeIds: filtered && filtered.length < stores.length ? filtered : undefined,
    };
  }, [search, stores, scope.pinnedStoreId]);
  const storeNames = useMemo(
    () =>
      scope.pinnedStoreId || stores.length < 2
        ? undefined
        : new Map(stores.map((store) => [store.id, store.name])),
    [scope.pinnedStoreId, stores],
  );
  // The visitor table needs the room; it gives the thread its pane back once one is open.
  const wide = view === 'visitors' && !selectedId;

  return (
    <InboxScopeContext.Provider value={scope}>
      <InboxLiveProvider>
        <div className="flex h-[calc(100svh-3.5rem)] min-h-0 flex-col">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2">
            <h1 className="flex items-center gap-2 text-base font-semibold">
              <MessagesSquare className="size-4" aria-hidden />
              {t('title')}
            </h1>
            <Tabs
              value={view}
              onValueChange={(value) =>
                onSearchChange({
                  view: value === 'conversations' ? undefined : (value as InboxPane),
                })
              }
            >
              <TabsList aria-label={t('views.label')}>
                {INBOX_VIEWS.map((pane: InboxPane) => (
                  <TabsTrigger key={pane} value={pane}>
                    {t(`views.${pane}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="ml-auto">
              <InboxToolbar />
            </div>
          </div>
          <PushInboxHint className="rounded-none border-x-0 border-t-0" />
          <div className="flex min-h-0 flex-1">
            <div
              className={cn(
                'min-h-0 w-full shrink-0 border-r',
                wide ? 'lg:w-auto lg:flex-1 lg:border-r-0' : 'lg:w-[360px]',
                selectedId ? 'hidden lg:block' : 'block',
              )}
            >
              {view === 'visitors' ? (
                <VisitorsPanel
                  scope={visitorsScope}
                  storeNames={storeNames}
                  compact={Boolean(selectedId)}
                  threadLink={scope.threadLink}
                />
              ) : (
                <InboxList
                  search={search}
                  onSearchChange={onSearchChange}
                  stores={stores}
                  pinnedStoreId={scope.pinnedStoreId}
                  selectedId={selectedId}
                  threadLink={scope.threadLink}
                />
              )}
            </div>
            <div
              className={cn(
                'min-h-0 min-w-0 flex-1',
                selectedId ? 'block' : wide ? 'hidden' : 'hidden lg:block',
              )}
            >
              <Outlet />
            </div>
          </div>
        </div>
      </InboxLiveProvider>
    </InboxScopeContext.Provider>
  );
}

function OrgInbox() {
  const search = useSearch({ from: ORG_ROUTE });
  const navigate = useNavigate({ from: '/org/inbox' });
  const onSearchChange = useCallback(
    (patch: Partial<InboxSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }) }),
    [navigate],
  );
  const scope = useMemo(() => orgInboxScope(search), [search]);
  return <InboxView scope={scope} search={search} onSearchChange={onSearchChange} />;
}

function StoreInbox() {
  const search = useSearch({ from: STORE_ROUTE });
  const { storeId } = useParams({ from: STORE_ROUTE });
  const navigate = useNavigate({ from: '/s/$storeId/inbox' });
  const onSearchChange = useCallback(
    (patch: Partial<InboxSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }) }),
    [navigate],
  );
  const scope = useMemo(() => storeInboxScope(storeId, search), [storeId, search]);
  return <InboxView scope={scope} search={search} onSearchChange={onSearchChange} />;
}

/** The organisation-wide inbox: every store the member can access (D-103 Q19). */
export function OrgInboxPage() {
  return (
    <FeatureErrorBoundary name="conversations.inbox">
      <OrgInbox />
    </FeatureErrorBoundary>
  );
}

/** One store's inbox: the same view with the store filter pinned. */
export function StoreInboxPage() {
  return (
    <FeatureErrorBoundary name="conversations.inbox">
      <StoreInbox />
    </FeatureErrorBoundary>
  );
}

/** The thread pane before a conversation is picked. */
export function InboxEmptyPage() {
  const { t } = useTranslation('conversations');
  return (
    <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <MessagesSquare className="size-8" aria-hidden />
      <p className="text-sm">{t('empty.title')}</p>
      <p className="text-xs">{t('empty.shortcuts')}</p>
    </div>
  );
}
